import {
  formatAugmentRef,
  formatSplitAugmentRef,
  getAugmentBaseCode,
  getAugmentDisplaySvg,
  getOwnedStyleIds,
  getOwnedStyleIdsForPoolAugment,
  getPoolAugmentDisplaySvg,
  getTeamSideAugmentGroup,
  isOneGoalAugmentCode,
  parseAugmentRef
} from './augments';

const STORAGE_KEY = 'scoreclash.augmentStylePrefs';

function readPrefs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function writePrefs(all) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore quota / private mode */
  }
}

/** @returns {number | null | undefined} `undefined` = never chosen; `null` = default art */
export function getAugmentStylePreference(baseCode) {
  if (!baseCode) return undefined;
  const all = readPrefs();
  if (!(baseCode in all)) return undefined;
  const v = all[baseCode];
  if (v === 'default') return null;
  if (typeof v === 'number' && Number.isInteger(v) && v > 0) return v;
  return null;
}

/** @param {string} baseCode @param {number | null} styleId `null` = default art */
export function setAugmentStylePreference(baseCode, styleId) {
  if (!baseCode) return;
  const all = readPrefs();
  all[baseCode] = styleId == null ? 'default' : styleId;
  writePrefs(all);
}

/**
 * Augment ref to place on a match/clash slot (respects saved style when owned).
 * @param {string} baseCode
 * @param {unknown} inventory
 */
export function getPreferredAugmentRef(baseCode, inventory) {
  if (!baseCode) return baseCode;
  const owned = getOwnedStyleIds(inventory, baseCode);
  if (owned.length === 0) return baseCode;
  const pref = getAugmentStylePreference(baseCode);
  if (pref === undefined || pref === null) return baseCode;
  if (owned.includes(pref)) return formatAugmentRef(baseCode, pref);
  return baseCode;
}

/** Preference key for a pool carousel card (UI pooled augments use `uiValue`). */
export function getPoolAugmentStyleKey(poolValue) {
  if (!poolValue) return poolValue;
  const group = getTeamSideAugmentGroup(poolValue);
  return group ? group.uiValue : poolValue;
}

/** @returns {number | null | undefined} */
export function getPoolAugmentStylePreference(poolValue) {
  const key = getPoolAugmentStyleKey(poolValue);
  const pref = getAugmentStylePreference(key);
  if (pref !== undefined) return pref;
  const group = getTeamSideAugmentGroup(poolValue);
  if (group) {
    const fromVariant = getAugmentStylePreference(group.home);
    if (fromVariant !== undefined) return fromVariant;
  }
  return undefined;
}

/** Persist style for a pool card (home/away pairs share one preference). */
export function setPoolAugmentStylePreference(poolValue, styleId) {
  const group = getTeamSideAugmentGroup(poolValue);
  if (group) {
    setAugmentStylePreference(group.uiValue, styleId);
    for (const code of group.variants) {
      setAugmentStylePreference(code, styleId);
    }
    return;
  }
  setAugmentStylePreference(poolValue, styleId);
}

/**
 * When dragging a placed augment back to the pool, keep its art as the pool preference
 * so the card (and next place) do not fall back to default.
 * @param {string | null | undefined} augmentRef
 */
export function restorePoolStylePreferenceFromPlacedRef(augmentRef) {
  if (augmentRef == null || augmentRef === '') return;
  const parsed = parseAugmentRef(String(augmentRef));
  if (!parsed.code) return;
  const poolValue = getTeamSideAugmentGroup(parsed.code)?.uiValue ?? parsed.code;
  setPoolAugmentStylePreference(poolValue, parsed.styleId);
}

/**
 * SVG for the mobile touch-drag ghost.
 * Placed refs (`2x#2`) must use their own style — pool prefs are keyed by base/pool id, not the raw ref.
 * @param {string} dragValue
 * @param {unknown} [inventory]
 */
export function getTouchDragGhostSvg(dragValue, inventory) {
  if (!dragValue) return null;
  const parsed = parseAugmentRef(String(dragValue));
  if (parsed.styleId != null) {
    return getAugmentDisplaySvg(String(dragValue), inventory);
  }
  const poolKey = getTeamSideAugmentGroup(dragValue)?.uiValue ?? (parsed.code || dragValue);
  const pref = getPoolAugmentStylePreference(poolKey);
  const styleId = pref === undefined ? null : pref;
  return getPoolAugmentDisplaySvg(poolKey, styleId, inventory);
}

/**
 * Whether a placed augment base code belongs to this pool card (incl. team-side variants).
 * @param {string} placedBaseCode
 * @param {string} poolValue
 */
export function placedAugmentMatchesPool(placedBaseCode, poolValue) {
  if (!placedBaseCode || !poolValue) return false;
  if (getAugmentBaseCode(poolValue) === 'g') {
    return isOneGoalAugmentCode(placedBaseCode);
  }
  const group = getTeamSideAugmentGroup(poolValue);
  if (group) {
    return group.uiValue === placedBaseCode || group.variants.includes(placedBaseCode);
  }
  return getAugmentBaseCode(poolValue) === placedBaseCode;
}

/**
 * Styled ref for an already-placed augment when the pool art choice changes.
 * @param {string} placedBaseCode
 * @param {string} poolValue
 * @param {number | null} styleId
 * @param {unknown} inventory
 * @param {number[]} [targets]
 * @param {{ home: number, away: number, weight: number }|null} [split]
 */
export function formatPlacedAugmentRefForPoolStyle(
  placedBaseCode,
  poolValue,
  styleId,
  inventory,
  targets,
  split
) {
  if (!placedBaseCode) return placedBaseCode;
  const owned = getOwnedStyleIdsForPoolAugment(poolValue, inventory);
  const resolvedStyle = styleId != null && owned.includes(styleId) ? styleId : null;
  if (split && typeof split === 'object') {
    return formatSplitAugmentRef(placedBaseCode, resolvedStyle, split);
  }
  if (resolvedStyle != null) {
    return formatAugmentRef(placedBaseCode, resolvedStyle, targets);
  }
  if (Array.isArray(targets) && targets.length > 0) {
    return formatAugmentRef(placedBaseCode, null, targets);
  }
  return placedBaseCode;
}

/**
 * Rewrite match / clash / round augment refs on a round when pool art changes.
 * Returns the same round object when nothing changes.
 * @param {object} round
 * @param {string} poolValue
 * @param {number | null} styleId
 * @param {unknown} inventory
 */
export function applyPoolStyleToRoundBets(round, poolValue, styleId, inventory) {
  if (!round || !poolValue) return round;

  let changed = false;
  const betsIn = round.bets && typeof round.bets === 'object' ? round.bets : {};
  const nextBets = { ...betsIn };

  for (const gid of Object.keys(betsIn)) {
    const bet = betsIn[gid];
    if (!bet || typeof bet !== 'object') continue;
    let nextBet = bet;
    let betChanged = false;

    if (bet.augment) {
      const base = getAugmentBaseCode(bet.augment);
      if (placedAugmentMatchesPool(base, poolValue)) {
        const parsed = parseAugmentRef(bet.augment);
        const nextRef = formatPlacedAugmentRefForPoolStyle(
          base,
          poolValue,
          styleId,
          inventory,
          parsed.targets,
          parsed.split
        );
        if (nextRef !== bet.augment) {
          nextBet = { ...nextBet, augment: nextRef };
          betChanged = true;
        }
      }
    }

    if (bet.clashAugments && typeof bet.clashAugments === 'object') {
      let nextCa = null;
      for (const [k, v] of Object.entries(bet.clashAugments)) {
        if (v == null || v === '') continue;
        const base = getAugmentBaseCode(String(v));
        if (!placedAugmentMatchesPool(base, poolValue)) continue;
        const nextRef = formatPlacedAugmentRefForPoolStyle(base, poolValue, styleId, inventory);
        if (nextRef === v) continue;
        if (!nextCa) nextCa = { ...bet.clashAugments };
        nextCa[k] = nextRef;
      }
      if (nextCa) {
        nextBet = betChanged ? { ...nextBet, clashAugments: nextCa } : { ...bet, clashAugments: nextCa };
        betChanged = true;
      }
    }

    if (betChanged) {
      nextBets[gid] = nextBet;
      changed = true;
    }
  }

  let nextRoundAugment = round.roundAugment ?? null;
  if (round.roundAugment) {
    const parsed = parseAugmentRef(round.roundAugment);
    const base = parsed.code;
    if (placedAugmentMatchesPool(base, poolValue)) {
      const nextRef = formatPlacedAugmentRefForPoolStyle(
        base,
        poolValue,
        styleId,
        inventory,
        parsed.targets
      );
      if (nextRef !== round.roundAugment) {
        nextRoundAugment = nextRef;
        changed = true;
      }
    }
  }

  if (!changed) return round;

  const out = { ...round, bets: nextBets };
  if (nextRoundAugment !== (round.roundAugment ?? null)) {
    out.roundAugment = nextRoundAugment;
  }
  return out;
}

/**
 * Final augment ref when placing from pool drag or clash pick.
 * @param {string} resolvedBaseCode backend code after drop resolution
 * @param {{ source?: string, value?: string, styleId?: number | null }} dragData
 * @param {unknown} inventory
 */
export function resolvePlacedAugmentRef(resolvedBaseCode, dragData, inventory) {
  if (!resolvedBaseCode) return resolvedBaseCode;

  if (dragData?.source === 'pool' && dragData.value) {
    const poolValue = dragData.value;
    let styleId =
      dragData.styleId !== undefined ? dragData.styleId : getPoolAugmentStylePreference(poolValue);
    if (styleId === undefined) styleId = null;
    const owned = getOwnedStyleIdsForPoolAugment(poolValue, inventory);
    if (styleId != null && owned.includes(styleId)) {
      return formatAugmentRef(resolvedBaseCode, styleId);
    }
    return resolvedBaseCode;
  }

  if (dragData?.source === 'game' && dragData.value) {
    const parsed = parseAugmentRef(String(dragData.value));
    if (parsed.split) {
      return formatSplitAugmentRef(resolvedBaseCode, parsed.styleId, parsed.split);
    }
    if (parsed.styleId != null) return formatAugmentRef(resolvedBaseCode, parsed.styleId);
  }

  return getPreferredAugmentRef(resolvedBaseCode, inventory);
}
